import { sidebarOpenTabAtom, SidebarTab } from '@/atoms/persistedPreferencesAtoms';
import { IS_SIDEBAR_PANEL_ENABLED } from '@/components/constants/flags';
import { UploadSessionScheduleModal } from '@/components/layout/lazy-session-dialogs';
import { useVisibleSidebarPanelContent } from '@/components/layout/Navigation/sidebar-panel/hooks/use-visible-sidebar-panel-content';
import { OfflineGatedTooltip } from '@/components/offline-recording/offline-gated-tooltip';
import { useSettingsModal } from '@/features/settings/hooks/useSettingsModal';
import { useIntegrationsMarketplaceVisibilityGate } from '@/features/settings/integrations/integrations-marketplace-visibility-gate';
import SidebarSessionUploadAreaOverlay from '@/features/upcomingSessionTab/components/sidebar-session-upload-area-overlay';
import { UpcomingSessionsList } from '@/features/upcomingSessionTab/components/upcoming-sessions-list';
import { useSidebarSchedulePaste } from '@/features/upcomingSessionTab/hooks/useSidebarSchedulePaste';
import { useSidebarUploadArea } from '@/features/upcomingSessionTab/hooks/useSidebarUploadArea';
import { PERMISSION_SUBJECTS } from '@/helpers/casl/ability';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useIsReviewingOtherUser } from '@/hooks/query/useQueryUser';
import { useRBACPermissionCheck } from '@/hooks/useRBACPermissionCheck';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { useTranslations } from '@heidi/runtime/i18n';
import { isUploadSessionScheduleModalOpenAtom } from '@heidi/session';
import { useAtomValue, useSetAtom } from 'jotai';
import { ArrowUpToLine, PlugZap } from 'lucide-react';
import { memo, useMemo } from 'react';

const SessionBarUpcomingTabV2 = memo(() => {
  const { open: openSettingsModal } = useSettingsModal();
  const { t } = useTranslations();
  const marketplaceVisibility = useIntegrationsMarketplaceVisibilityGate();
  const showMarketplaceCta = marketplaceVisibility === 'visible';
  const setIsUploadSessionScheduleModalOpen = useSetAtom(
    isUploadSessionScheduleModalOpenAtom
  );
  const { integrationEhrProvider } = useIntegrationEhrInfo();
  const sidebarOpenTab = useAtomValue(sidebarOpenTabAtom);
  const isMobile = useIsMobile();
  const visibleSidebarPanelContent = useVisibleSidebarPanelContent();
  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();
  const isSidebarVisibleForPaste =
    !isMobile &&
    (isSidebarPanelEnabled
      ? visibleSidebarPanelContent === 'sessions'
      : sidebarOpenTab === SidebarTab.SESSION_LIST);
  // Preserve the existing inner transition; the parent panel controls actual visibility.
  const shouldKeepSessionListContentVisible =
    isSidebarPanelEnabled || Boolean(sidebarOpenTab);
  const { showSidebarUploadArea, handleDragEnter, handleDragLeave, handleDrop } =
    useSidebarUploadArea();

  const { checkRBACPermission } = useRBACPermissionCheck();
  const canCreateSessions = useMemo(() => {
    return checkRBACPermission(
      PERMISSION_SUBJECTS.TEAM_OTHERS_SESSION_CREATE,
      PERMISSION_SUBJECTS.SESSION_CREATE
    );
  }, [checkRBACPermission]);

  const isReviewingOtherUser = useIsReviewingOtherUser();
  const showIntegrationSetupButton =
    !integrationEhrProvider && !isReviewingOtherUser && showMarketplaceCta;
  // Upload opens a server-backed schedule flow; grey it out while offline-gated
  // (APP-7516). The upcoming-sessions list stays browsable offline.
  const { shouldGateControls: isOfflineGated } = useOfflineGating();

  useSidebarSchedulePaste({ enabled: canCreateSessions && isSidebarVisibleForPaste });

  return (
    <div
      className={cn(
        'flex h-full flex-col items-center justify-center gap-2 transition-all',
        shouldKeepSessionListContentVisible
          ? 'translate-y-0 opacity-100 duration-1000'
          : '-translate-y-5 opacity-0'
      )}
    >
      <div
        className="relative flex min-h-0 w-full flex-auto flex-col overflow-hidden"
        data-testid="upcoming-sessions-drag-target"
        onDragEnter={handleDragEnter}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <UpcomingSessionsList />
        {canCreateSessions && <SidebarSessionUploadAreaOverlay />}
      </div>
      {!showSidebarUploadArea && canCreateSessions && (
        <OfflineGatedTooltip isOfflineGated={isOfflineGated} className="w-full">
          <Button
            disableAutomaticSvgSizing
            className="w-full gap-1"
            size="xs"
            data-testid="upcoming-sessions-upload-button"
            disabled={isOfflineGated}
            onClick={
              isOfflineGated ? undefined : () => setIsUploadSessionScheduleModalOpen(true)
            }
          >
            <ArrowUpToLine size={12} />
            {t('sessions.upcomingSessions.addUpcomingSessions')}
          </Button>
        </OfflineGatedTooltip>
      )}
      {showIntegrationSetupButton ? (
        <Button
          disableAutomaticSvgSizing
          className="w-full gap-1"
          size="xs"
          variant="secondary"
          type="button"
          onClick={() => {
            openSettingsModal('/settings/integrations', 'sidebar');
          }}
        >
          <PlugZap size={12} />
          {t('integrations.setupIntegration')}
        </Button>
      ) : null}

      {canCreateSessions && <UploadSessionScheduleModal />}
    </div>
  );
});

SessionBarUpcomingTabV2.displayName = 'SessionBarUpcomingTabV2';
export default SessionBarUpcomingTabV2;
